import React, { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { Module } from "@/constants";
import { fetchUser } from "@/services/users";
import { UsersContext } from "./UsersContext";
import { useUserLoading } from "../hooks/useUserLoading";
import { useUserError } from "../hooks/useUserError";
import { useUserHandlers } from "../hooks/useUserHandlers";
import { useUserDialog } from "../hooks/useUserDialog";
import { UserConfirmDialog } from "../components/UserConfirmDialog";

interface UsersProviderProps {
  children: React.ReactNode;
  userId?: string;
}

export const UsersProvider: React.FC<UsersProviderProps> = ({
  children,
  userId = "",
}) => {
  const userQuery = useQuery({
    queryKey: [Module.Users, userId],
    queryFn: () => fetchUser(userId),
    enabled: !!userId,
  });

  const { dialogHandlerKey, openDialog, closeDialog, confirmDialog } =
    useUserDialog();

  const {
    mutations,
    handleActivate,
    handleDeactivate,
    handleUpdate,
    handleCreate,
  } = useUserHandlers({
    userId,
    openDialog,
    closeDialog,
  });

  const loadingState = useUserLoading({ userQuery }, mutations);
  const errorState = useUserError({ userQuery }, mutations);

  const clearError = useCallback(() => {
    mutations.activateUserMutation.reset();
    mutations.deactivateUserMutation.reset();
    mutations.updateUserMutation.reset();
    mutations.createUserMutation.reset();
  }, [mutations]);

  const refreshUser = useCallback(() => {
    userQuery.refetch();
  }, [userQuery]);

  const contextValue = {
    query: userQuery,
    data: userQuery.data,
    errorState,
    loadingState,
    clearError,
    refreshUser,
    handleActivate,
    handleDeactivate,
    handleUpdate,
    handleCreate,
  };

  return (
    <UsersContext.Provider value={contextValue}>
      {children}
      <UserConfirmDialog
        handlerKey={dialogHandlerKey}
        onConfirm={confirmDialog}
        onCancel={closeDialog}
      />
    </UsersContext.Provider>
  );
};
